import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ColDef, GridApi } from "ag-grid-community";
import { Button, Dropdown, Input, Tooltip } from "antd";
import type { MenuProps } from "antd";
import type { InputRef } from "antd";
import { Columns, DotsThreeVertical, MagnifyingGlass } from "@phosphor-icons/react";
import Breadcrumb from "../../../Layout/Breadcrumb/Breadcrumb.js";
import AgGrid from "../../shared/grid/AgGrid.js";
import LoadingRing from "../../shared/LoadingRing.jsx";
import useMobileGrid from "../../shared/grid/useMobileGrid.ts";
import { PlatformIconButton } from "../../../features/platform-admin/PlatformAdminIcon.tsx";
import { getDirection, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import "./AccountOrders.css";

export type AccountOrderColumn<T> = ColDef<T> & {
  suppressSharedOperationMenu?: boolean;
  /** Opt in to the compact view; other columns remain available through All columns. */
  mobileEssential?: boolean;
};

const defaultColDef = {
  editable: false, sortable: true, resizable: true, filter: "agTextColumnFilter",
  floatingFilter: true, enableRowGroup: true, flex: 1, minWidth: 90,
};

// Legacy command declaration; AgGrid renders these items in its shared row toolbar.
export function AccountOrderMenu({ items, pending = false, disabled = false }: {
  items: MenuProps["items"]; pending?: boolean; disabled?: boolean;
}) {
  return (
    <Dropdown
      trigger={["click"]}
      menu={{ items }}
      disabled={disabled}
    >
      <Tooltip title={t("عملیات")}>
        <Button
          type="text"
          disabled={disabled}
          loading={pending}
          icon={<DotsThreeVertical size={20} />}
        >
          <span className="platform-admin__sr-only">{t("عملیات")}</span>
        </Button>
      </Tooltip>
    </Dropdown>
  );
}

AccountOrderMenu.gridOperations = true;

export default function AccountOrderGrid<T extends { id: number | string }>({ id, title, filterBy = "title", rows, columns, actions, backAction, loading, onRefresh, footer, loadingTip, mobileRowActions = false }: {
  id: string; title: string; rows: T[]; columns: AccountOrderColumn<T>[]; actions: ReactNode;
  filterBy?: string;
  backAction?: ReactNode;
  loading: boolean; onRefresh?: () => void; footer?: ReactNode;
  loadingTip?: string;
  mobileRowActions?: boolean;
}) {
  useStoreLanguage();
  const desktopBackAction = backAction ? (
    <div className="account-order-grid__desktop-back-action">
      {backAction}
    </div>
  ) : null;
  const api = useRef<GridApi<T>>();
  const viewport = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInput = useRef<InputRef>(null);
  const searchTrigger = useRef<HTMLSpanElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const mobile = useMobileGrid();
  useEffect(function () {
    if (mobile && searchOpen) searchInput.current?.focus();
  }, [mobile, searchOpen]);
  function closeSearch() {
    setSearch("");
    api.current?.setGridOption("quickFilterText", "");
    setSearchOpen(false);
    requestAnimationFrame(function () { searchTrigger.current?.querySelector("button")?.focus(); });
  }
  const [allColumns, setAllColumns] = useState(false);
  const hasCompactColumns = columns.some(function (column) { return column.mobileEssential; });
  useEffect(function () {
    if (mobile) {
      setGrouping(false);
      setFullscreen(false);
    }
  }, [mobile]);
  useEffect(function () {
    if (!fullscreen) return;
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape" && !event.defaultPrevented) setFullscreen(false);
    }
    document.addEventListener("keydown", escape);
    return function () { document.removeEventListener("keydown", escape); };
  }, [fullscreen]);
  const hasFooter = Boolean(footer);
  const getRowId = useCallback(function (params: { data: T }) {
    return String(params.data.id);
  }, []);
  const setGridApi = useCallback(function (gridApi: GridApi<T>) {
    api.current = gridApi;
    const shell = viewport.current?.closest<HTMLElement>(".platform-admin");
    if (shell) gridApi.setGridOption("popupParent", shell);
  }, []);
  const gridOptions = useMemo(function () {
    return {
      defaultColDef,
      quickFilterText: search,
      rowHeight: 42,
      headerHeight: 48,
      suppressCellFocus: false,
      rowGroupPanelShow: grouping ? "always" : "never",
      statusBar: hasFooter ? undefined : { statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent", align: "right" },
      ] },
    };
  }, [grouping, search, hasFooter]);
  const grid = useMemo(function () {
    return (
      <AgGrid
        id={id}
        filterBy={filterBy}
        data={rows}
        columns={columns}
        mobileRowActions={mobileRowActions}
        getRowId={getRowId}
        setGridApi={setGridApi}
        hideOverlay
        disableSearch
        loading={false}
        actionsDisabled={loading}
        groupingPanelOpen={grouping}
        groupPanel={grouping ? "always" : "disable"}
        gridOptions={gridOptions}
        containerLayout
        compactColumns={hasCompactColumns}
        showAllMobileColumns={allColumns || grouping}
      />
    );
  }, [id, filterBy, rows, columns, loading, grouping, gridOptions, getRowId, setGridApi, hasCompactColumns, allColumns, mobileRowActions]);
  const searchControl = (
    <div className={"account-order-grid__search-controls" + (mobile && searchOpen ? " is-expanded" : "")}>
      {!mobile || searchOpen ? <Input
        ref={searchInput}
        id={id + "-search"}
        className="platform-admin__grid-search"
        aria-label={t("جستجو")}
        placeholder={t("جستجو کنید ...")}
        prefix={<MagnifyingGlass size={17} />}
        value={search}
        allowClear
        onKeyDown={function (event) {
          if (mobile && event.key === "Escape") {
            event.stopPropagation();
            closeSearch();
          }
        }}
        onChange={function (event) {
          setSearch(event.target.value);
          api.current?.setGridOption("quickFilterText", event.target.value);
        }}
      /> : (
        <span ref={searchTrigger}>
          <PlatformIconButton
            label={t("جستجو")}
            icon="search"
            aria-expanded={searchOpen}
            aria-controls={id + "-search"}
            onClick={function () { setSearchOpen(true); }}
          />
        </span>
      )}
      {mobile && !searchOpen && onRefresh ? (
        <PlatformIconButton
          label={t("تازه‌سازی")}
          icon="refresh"
          disabled={loading}
          loading={loading}
          onClick={onRefresh}
        />
      ) : null}
      {mobile && searchOpen ? (
        <PlatformIconButton
          label={t("بستن")}
          icon="close"
          onClick={closeSearch}
        />
      ) : null}
      {mobile && !searchOpen && hasCompactColumns ? (
        <Button
          className="account-order-grid__columns-toggle"
          icon={backAction ? <Columns size={16} /> : undefined}
          aria-label={allColumns ? t("ستون‌های اصلی") : t("همه ستون‌ها")}
          title={allColumns ? t("ستون‌های اصلی") : t("همه ستون‌ها")}
          aria-pressed={allColumns}
          disabled={grouping}
          onClick={function () { setAllColumns(function (current) { return !current; }); }}
        >
          {!backAction ? (allColumns ? t("ستون‌های اصلی") : t("همه ستون‌ها")) : null}
        </Button>
      ) : null}
    </div>
  );
  return (
    <div className={"account-order-grid platform-admin__grid-card" + (fullscreen ? " account-order-grid--fullscreen" : "")}>
      <div className={"platform-admin__toolbar" + (mobile && searchOpen ? " is-searching" : "") + (mobile && backAction ? " has-back-action" : "")}>
        {!mobile && getDirection() === "ltr" ? desktopBackAction : null}
        {!mobile || !searchOpen ? <>
        <Breadcrumb
          className="account-order-grid__actions"
          showGuides={false}
          items={[{ title }]}
          actions={actions}
        />
        {!mobile ? searchControl : null}
        {!mobile ? (
        <div className="platform-admin__grid-tools">
          {!mobile ? (
            <PlatformIconButton
              label={t("گروه‌بندی ستون‌ها")}
              icon="group"
              aria-pressed={grouping}
              onClick={function () { setGrouping(function (current) { return !current; }); }}
            />
          ) : null}
          {onRefresh ? (
            <PlatformIconButton
              label={t("تازه‌سازی")}
              icon="refresh"
              disabled={loading}
              loading={loading}
              onClick={onRefresh}
            />
          ) : null}
          {!mobile ? (
            <PlatformIconButton
              label={fullscreen ? t("خروج از تمام صفحه") : t("تمام صفحه")}
              icon={fullscreen ? "exitFullscreen" : "fullscreen"}
              onClick={function () { setFullscreen(!fullscreen); }}
            />
          ) : null}
        </div>
        ) : null}
        </> : null}
        {!mobile && getDirection() === "rtl" ? desktopBackAction : null}
        {mobile ? searchControl : null}
        {mobile && backAction ? (
          <div className="account-order-grid__back-action">
            {backAction}
          </div>
        ) : null}
      </div>
      <div
        ref={viewport}
        className="platform-admin__grid-viewport"
        aria-busy={loading}
      >
        {grid}
        {loading ? <LoadingRing overlay tip={loadingTip || t("در حال دریافت اطلاعات سفارش")} /> : null}
      </div>
      {footer ? <div className="platform-admin__footer">{footer}</div> : null}
    </div>
  );
}
